import React, { useRef, useState } from 'react';
import Button from '../../components/ui/Button/Button.jsx';
import TextField from '../../components/ui/TextField/TextField.jsx';
import {
  processCardNumberInput, processCVVInput, processExpirationDateInput,
} from '../../components/Forms/cardUtils.js';
import { validateCard } from './cardValidation.js';

const API_FIELD_MESSAGES = {
  number: 'This card number was not accepted. Please check it and enter it again.',
  expiry: 'This expiry date was not accepted. Please check it and enter it again.',
  cvv: 'This security code was not accepted. Please check it and enter it again.',
};

/**
 * The card form. The API takes the number, the expiry and the security
 * code — no cardholder name. The card lives in this component's state only
 * while it is being typed: the fields are emptied the moment the card is
 * handed to `onSubmit`, before the request has even been answered.
 */
export default function PayCardForm({ submitLabel, apiField = null, disabled = false, onSubmit }) {
  const [number, setNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [errors, setErrors] = useState({});
  // The API's complaint about a field stays until that field is edited.
  const [apiFieldShown, setApiFieldShown] = useState(apiField);
  const sent = useRef(false);

  const shown = {
    ...(apiFieldShown ? { [apiFieldShown]: API_FIELD_MESSAGES[apiFieldShown] } : {}),
    ...errors,
  };

  const edit = (field, setter, format) => (e) => {
    setter(format(e.target.value));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    if (apiFieldShown === field) setApiFieldShown(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (disabled || sent.current) return;
    const checked = validateCard({ number, expiry, cvv });
    if (!checked.ok) {
      setErrors(checked.errors);
      return;
    }
    sent.current = true;
    setNumber('');
    setExpiry('');
    setCvv('');
    setErrors({});
    onSubmit(checked.card);
  };

  return (
    <form className="pay-form" onSubmit={handleSubmit} noValidate data-testid="pay-card-form">
      <TextField
        label="Card number"
        name="cardnumber"
        value={number}
        onChange={edit('number', setNumber, processCardNumberInput)}
        autoComplete="cc-number"
        inputMode="numeric"
        placeholder="1234 5678 9012 3456"
        error={shown.number}
        disabled={disabled}
        data-testid="pay-card-number"
      />
      <div className="pay-form-row">
        <TextField
          label="Expiry (MM/YY)"
          name="cc-exp"
          value={expiry}
          onChange={edit('expiry', setExpiry, processExpirationDateInput)}
          autoComplete="cc-exp"
          inputMode="numeric"
          placeholder="MM/YY"
          error={shown.expiry}
          disabled={disabled}
          data-testid="pay-card-expiry"
        />
        <TextField
          label="Security code"
          name="cvc"
          value={cvv}
          onChange={edit('cvv', setCvv, processCVVInput)}
          autoComplete="cc-csc"
          inputMode="numeric"
          placeholder="CVV"
          error={shown.cvv}
          disabled={disabled}
          data-testid="pay-card-cvv"
        />
      </div>
      <Button type="submit" fullWidth disabled={disabled} data-testid="pay-submit">
        {submitLabel}
      </Button>
    </form>
  );
}
